import React, { createContext, useContext, useCallback, useState } from 'react';
import { getErrorMessage, getErrorCode, isAPIError } from '../services/api';

interface ErrorContextType {
  errors: AppError[];
  addError: (error: unknown, context?: string) => void;
  removeError: (id: string) => void;
  clearErrors: () => void;
  hasErrors: boolean;
}

interface AppError {
  id: string;
  message: string;
  code: string;
  context?: string;
  timestamp: Date;
  isRetryable: boolean;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

export const useErrorHandler = () => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useErrorHandler must be used within an ErrorProvider');
  }
  return context;
};

interface ErrorProviderProps {
  children: React.ReactNode;
  maxErrors?: number;
}

export const ErrorProvider: React.FC<ErrorProviderProps> = ({ 
  children, 
  maxErrors = 5 
}) => {
  const [errors, setErrors] = useState<AppError[]>([]);

  const addError = useCallback((error: unknown, context?: string) => {
    const errorId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const message = getErrorMessage(error);
    const code = getErrorCode(error);
    
    // Determine if error is retryable
    const isRetryable = isAPIError(error) && 
      (error.response?.status === 429 || 
       (error.response?.status !== undefined && error.response.status >= 500));

    const appError: AppError = {
      id: errorId,
      message,
      code,
      context,
      timestamp: new Date(),
      isRetryable,
    };

    setErrors(prev => {
      const updated = [appError, ...prev].slice(0, maxErrors);
      return updated;
    });

    // Auto-remove error after 10 seconds for non-critical errors
    if (!isRetryable && code !== 'NETWORK_ERROR') {
      setTimeout(() => {
        setErrors(prev => prev.filter(e => e.id !== errorId));
      }, 10000);
    }

    // Log error for debugging
    console.error(`[Error Handler] ${context || 'Unknown context'}:`, {
      message,
      code,
      error,
      timestamp: appError.timestamp,
    });
  }, [maxErrors]);

  const removeError = useCallback((id: string) => {
    setErrors(prev => prev.filter(error => error.id !== id));
  }, []);

  const clearErrors = useCallback(() => {
    setErrors([]);
  }, []);

  const hasErrors = errors.length > 0;

  const value: ErrorContextType = {
    errors,
    addError,
    removeError,
    clearErrors,
    hasErrors,
  };

  return (
    <ErrorContext.Provider value={value}>
      {children}
      <ErrorNotifications />
    </ErrorContext.Provider>
  );
};

// Error notifications component
const ErrorNotifications: React.FC = () => {
  const { errors, removeError } = useErrorHandler();

  if (errors.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2">
      {errors.slice(0, 3).map((error) => (
        <ErrorNotification
          key={error.id}
          error={error}
          onDismiss={() => removeError(error.id)}
        />
      ))}
    </div>
  );
};

// Individual error notification
const ErrorNotification: React.FC<{
  error: AppError;
  onDismiss: () => void;
}> = ({ error, onDismiss }) => {
  const [isVisible, setIsVisible] = useState(true);

  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(onDismiss, 300); // Allow animation to complete
  };

  const getErrorIcon = () => {
    if (error.code === 'NETWORK_ERROR') {
      return (
        <svg className="h-5 w-5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      );
    }

    return (
      <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    );
  };

  const getBgColor = () => {
    if (error.code === 'NETWORK_ERROR') return 'bg-yellow-900 border-yellow-700';
    if (error.isRetryable) return 'bg-orange-900 border-orange-700';
    return 'bg-red-900 border-red-700';
  };

  return (
    <div
      className={`
        ${getBgColor()}
        border rounded-lg p-4 shadow-lg max-w-sm transition-all duration-300
        ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full'}
      `}
    >
      <div className="flex items-start">
        <div className="flex-shrink-0">
          {getErrorIcon()}
        </div>
        <div className="ml-3 flex-1">
          <p className="text-sm font-medium text-white">
            {error.context && `${error.context}: `}
            {error.message}
          </p>
          {error.isRetryable && (
            <p className="mt-1 text-xs text-gray-300">
              This error might be temporary. Please try again.
            </p>
          )}
        </div>
        <div className="ml-4 flex-shrink-0">
          <button
            onClick={handleDismiss}
            className="inline-flex text-gray-400 hover:text-gray-200 focus:outline-none focus:text-gray-200"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ErrorProvider;